import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { ChevronDown, ChevronRight, Loader2, PiggyBank, Wallet } from "lucide-react";

import useApiMethods from "../../../lib/useApiMethods";
import { EMPLOYEE_DEPOSITS } from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";
import ToastMsg from "../../../components/common/ToastMsg";
import { DepositMovementTrace } from "../../../components/employee/DepositMovementTrace";

// Read-only by design. An employee may see what the organisation holds for them
// and how it got to that number, but a deposit is the organisation's record of
// what it owes — nothing here changes it. The API refuses self-service writes
// too, so this screen is not the only thing standing in the way.

const PURPOSE_LABEL = {
    security: "Security",
    equipment: "Equipment",
    uniform: "Uniform",
    accommodation: "Accommodation",
    other: "Other",
};

const MODE_LABEL = {
    cash: "Cash",
    bank_transfer: "Bank transfer",
    cheque: "Cheque",
    salary_deduction: "Deducted from salary",
};

// What each movement means to the employee reading it, in their words rather
// than the ledger's.
const MOVEMENT_LABEL = {
    collected: "Collected from you",
    applied: "Used against dues",
    refunded: "Refunded to you",
    forfeited: "Kept by the organisation",
    reversed: "Correction",
};

const STATUS_CHIP = {
    active: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    closed: "bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300",
    forfeited: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
};

const STATUS_LABEL = { active: "Active", closed: "Closed", forfeited: "Forfeited" };

const money = (value) =>
    Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const shownDate = (value) => (value ? dayjs(value).format("DD MMM YYYY") : "—");

const MyProfileDepositsTab = () => {
    const { fetch } = useApiMethods();
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    const [loading, setLoading] = useState(true);
    const [deposits, setDeposits] = useState([]);
    const [openId, setOpenId] = useState(null);
    const [statements, setStatements] = useState({});
    const [loadingStatement, setLoadingStatement] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        // `mine: true` and nothing else: the server pins the list to the token's own
        // employee. Sending an id from here would hand a manager their whole team
        // on their own profile page.
        const response = await fetchRef.current(EMPLOYEE_DEPOSITS, { mine: true, page_size: 100 });
        setLoading(false);
        if (response?.status === "success") setDeposits(response.data?.result || []);
        else if (response?.status === "fail") ToastMsg("error", response.message);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const toggle = async (deposit) => {
        if (openId === deposit.id) {
            setOpenId(null);
            return;
        }
        setOpenId(deposit.id);
        if (statements[deposit.id]) return;
        setLoadingStatement(true);
        const response = await fetchRef.current(EMPLOYEE_DEPOSITS, { id: deposit.id });
        setLoadingStatement(false);
        if (response?.status === "success") {
            setStatements((prev) => ({ ...prev, [deposit.id]: response.data?.statement || [] }));
        } else {
            ToastMsg("error", response?.message || "Could not load the statement");
        }
    };

    const total = (key) => deposits.reduce((sum, d) => sum + Number(d[key] || 0), 0);
    const totalHeld = total("balance");
    const totalUsed = total("applied") + total("forfeited");

    if (loading) {
        return (
            <div className="flex items-center justify-center py-14 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your deposits…
            </div>
        );
    }

    if (!deposits.length) {
        return (
            <div className="flex flex-col items-center justify-center py-14 text-center">
                <PiggyBank className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    No deposits on your record
                </p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                    The organisation is not holding any deposit for you. If you have paid one and it is
                    not shown here, please raise it with HR.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* The one number the employee opened this tab for. */}
            <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3.5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2.5">
                        <Wallet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        <div>
                            <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">
                                Remaining deposit held for you
                            </p>
                            <p className="text-[11px] text-emerald-700/80 dark:text-emerald-400/80">
                                Refundable when you are relieved, less anything already used
                            </p>
                        </div>
                    </div>
                    <p className="text-2xl font-semibold tabular-nums text-emerald-900 dark:text-emerald-200">
                        ₹ {money(totalHeld)}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-2.5">
                {[
                    { label: "Paid by you", value: total("collected") },
                    { label: "Used against dues", value: totalUsed },
                    { label: "Refunded to you", value: total("refunded") },
                ].map((tile) => (
                    <div
                        key={tile.label}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 px-3 py-2.5"
                    >
                        <p className="text-[11px] text-muted-foreground">{tile.label}</p>
                        <p className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100 mt-0.5">
                            ₹ {money(tile.value)}
                        </p>
                    </div>
                ))}
            </div>

            <div className="space-y-2.5">
                {deposits.map((deposit) => {
                    const expanded = openId === deposit.id;
                    const rows = statements[deposit.id];
                    return (
                        <div
                            key={deposit.id}
                            className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                        >
                            <button
                                type="button"
                                onClick={() => toggle(deposit)}
                                className="w-full flex items-center justify-between gap-3 px-3.5 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                            >
                                <div className="flex items-center gap-2.5 min-w-0">
                                    {expanded ? (
                                        <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                                    ) : (
                                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                                    )}
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                            {PURPOSE_LABEL[deposit.purpose] || deposit.purpose} deposit
                                            <span
                                                className={cn(
                                                    "ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium align-middle",
                                                    STATUS_CHIP[deposit.status] || STATUS_CHIP.closed,
                                                )}
                                            >
                                                {STATUS_LABEL[deposit.status] || deposit.status}
                                            </span>
                                        </p>
                                        <p className="text-[11px] text-muted-foreground truncate">
                                            {MODE_LABEL[deposit.collection_mode] || deposit.collection_mode}
                                            {" · agreed ₹ "}
                                            {money(deposit.agreed_amount)}
                                            {" · opened "}
                                            {shownDate(deposit.opened_on)}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-[11px] text-muted-foreground">Remaining</p>
                                    <p className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                                        ₹ {money(deposit.balance)}
                                    </p>
                                </div>
                            </button>

                            {expanded && (
                                <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 px-3.5 py-3">
                                    {loadingStatement && !rows ? (
                                        <div className="flex items-center text-xs text-muted-foreground py-2">
                                            <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> Loading
                                            statement…
                                        </div>
                                    ) : !rows?.length ? (
                                        <p className="text-xs text-muted-foreground py-1">
                                            Nothing has been recorded against this deposit yet.
                                        </p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-xs">
                                                <thead className="text-muted-foreground">
                                                    <tr className="text-left">
                                                        <th className="py-1.5 pr-3 font-medium">Date</th>
                                                        <th className="py-1.5 pr-3 font-medium">What happened</th>
                                                        <th className="py-1.5 pr-3 font-medium">Reason</th>
                                                        <th className="py-1.5 pr-3 font-medium text-right">Amount</th>
                                                        <th className="py-1.5 font-medium text-right">Remaining</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                                    {rows.map((row) => (
                                                        <tr key={row.id}>
                                                            <td className="py-1.5 pr-3 whitespace-nowrap">
                                                                {shownDate(row.txn_date)}
                                                            </td>
                                                            <td className="py-1.5 pr-3">
                                                                <DepositMovementTrace txn={row} employeeView />
                                                            </td>
                                                            <td className="py-1.5 pr-3 text-muted-foreground">
                                                                {row.reason || row.reference || "—"}
                                                                {row.is_reversed && row.reversed_by_detail ? (
                                                                    <span className="block text-[10px] text-amber-700 dark:text-amber-400 mt-0.5">
                                                                        This entry was later corrected.
                                                                    </span>
                                                                ) : null}
                                                            </td>
                                                            <td
                                                                className={cn(
                                                                    "py-1.5 pr-3 text-right tabular-nums whitespace-nowrap",
                                                                    row.direction === "out"
                                                                        ? "text-rose-600 dark:text-rose-400"
                                                                        : "text-emerald-700 dark:text-emerald-400",
                                                                )}
                                                            >
                                                                {row.direction === "out" ? "− " : "+ "}₹{" "}
                                                                {money(row.amount)}
                                                            </td>
                                                            <td className="py-1.5 text-right tabular-nums whitespace-nowrap">
                                                                ₹ {money(row.balance)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <p className="text-[11px] text-muted-foreground">
                This is a record only. If something here looks wrong, raise it with HR — deposits cannot
                be changed from your own profile.
            </p>
        </div>
    );
};

export default MyProfileDepositsTab;
